/**
 * API para módulo NR-1 (Questionários de Riscos Ocupacionais)
 */

import request from "@/utils/request";

// ==================== QUESTIONÁRIOS ====================

/**
 * Listar questionários
 */
export function getQuestionnaires(params) {
  return request({
    url: "/nr1/questionnaires",
    method: "get",
    params,
  });
}

/**
 * Buscar questionário por ID
 */
export function getQuestionnaire(id) {
  return request({
    url: `/nr1/questionnaires/${id}`,
    method: "get",
  });
}

/**
 * Criar novo questionário
 */
export function createQuestionnaire(data) {
  return request({
    url: "/nr1/questionnaires",
    method: "post",
    data,
  });
}

/**
 * Clonar questionário
 */
export function cloneQuestionnaire(id, data) {
  return request({
    url: `/nr1/questionnaires/${id}/clone`,
    method: "post",
    data,
  });
}

/**
 * Atualizar questionário
 */
export function updateQuestionnaire(id, data) {
  return request({
    url: `/nr1/questionnaires/${id}`,
    method: "put",
    data,
  });
}

/**
 * Excluir questionário
 */
export function deleteQuestionnaire(id) {
  return request({
    url: `/nr1/questionnaires/${id}`,
    method: "delete",
  });
}

/**
 * Obter potenciais destinatários baseados nas áreas selecionadas
 */
export function getPotentialRecipients(id, areas) {
  return request({
    url: `/nr1/questionnaires/${id}/recipients`,
    method: "get",
    params: { areas: areas.join(",") },
  });
}

/**
 * Verificar se um e-mail já foi convidado para um questionário
 */
export function checkEmailParticipation(id, email) {
  return request({
    url: `/nr1/questionnaires/${id}/check-email/${email}`,
    method: "get",
  });
}

// ==================== ÁREAS ====================

/**
 * Listar áreas da empresa
 */
export function getAreas(params) {
  return request({
    url: "/nr1/areas",
    method: "get",
    params,
  });
}

/**
 * Listar unidades (business units) para NR-1
 */
export function getBusinessUnits(params) {
  return request({
    url: "/nr1/business-units",
    method: "get",
    params,
  });
}

// ==================== DISTRIBUIÇÃO ====================

/**
 * Distribuir questionário para áreas
 */
export function distributeQuestionnaire(id, data) {
  return request({
    url: `/nr1/questionnaires/${id}/distribute`,
    method: "post",
    data,
  });
}

/**
 * Listar participações
 */
export function getParticipations(id, params) {
  return request({
    url: `/nr1/questionnaires/${id}/participations`,
    method: "get",
    params,
  });
}

// ==================== QR CODE ====================

/**
 * Gerar QR Code
 */
export function generateQRCode(id, params) {
  return request({
    url: `/nr1/questionnaires/${id}/qrcode`,
    method: "get",
    params,
  });
}

/**
 * Download do QR Code
 */
export function downloadQRCode(id, params) {
  return request({
    url: `/nr1/questionnaires/${id}/qrcode/download`,
    method: "get",
    params,
    responseType: "blob",
  });
}

// ==================== ANALYTICS ====================

/**
 * Obter analytics do questionário
 */
export function getAnalytics(id, params) {
  return request({
    url: `/nr1/questionnaires/${id}/analytics`,
    method: "get",
    params,
  });
}

/**
 * Obter estatísticas do dashboard
 */
export function getDashboardStats(id, params) {
  return request({
    url: `/nr1/questionnaires/${id}/dashboard-stats`,
    method: "get",
    params,
  });
}

/**
 * Obter Análise IA do questionário
 */
export function getAiAnalysis(id, params) {
  return request({
    url: `/nr1/questionnaires/${id}/ai-analysis`,
    method: "get",
    params,
  });
}

/**
 * Obter evolução de risco
 */
export function getRiskEvolution(id, params) {
  return request({
    url: `/nr1/questionnaires/${id}/evolution`,
    method: "get",
    params,
  });
}

/**
 * Obter observações finais do questionário
 */
export function getObservations(id, params) {
  return request({
    url: `/nr1/questionnaires/${id}/observations`,
    method: "get",
    params,
  });
}

/**
 * Obter analytics macro do dashboard NR-1
 */
export function getMacroDashboardAnalytics(params) {
  return request({
    url: "/nr1/dashboard/analytics",
    method: "get",
    params,
  });
}

/**
 * Listar dashboards salvos do NR-1
 */
export function getDashboards(params) {
  return request({
    url: "/nr1/dashboards",
    method: "get",
    params,
  });
}

/**
 * Buscar dashboard salvo do NR-1
 */
export function getDashboard(id, params) {
  return request({
    url: `/nr1/dashboards/${id}`,
    method: "get",
    params,
  });
}

/**
 * Criar dashboard salvo do NR-1
 */
export function createDashboard(data) {
  return request({
    url: "/nr1/dashboards",
    method: "post",
    data,
  });
}

/**
 * Atualizar dashboard salvo do NR-1
 */
export function updateDashboard(id, data) {
  return request({
    url: `/nr1/dashboards/${id}`,
    method: "put",
    data,
  });
}

// ==================== PLANOS DE AÇÃO ====================

/**
 * Listar planos de ação
 */
export function getActionPlans(questionnaireId, params) {
  return request({
    url: `/nr1/questionnaires/${questionnaireId}/action-plans`,
    method: "get",
    params,
  });
}

/**
 * Criar plano de ação
 */
export function createActionPlan(data) {
  return request({
    url: "/nr1/action-plans",
    method: "post",
    data,
  });
}

/**
 * Atualizar plano de ação
 */
export function updateActionPlan(id, data) {
  return request({
    url: `/nr1/action-plans/${id}`,
    method: "put",
    data,
  });
}

/**
 * Excluir plano de ação
 */
export function deleteActionPlan(id) {
  return request({
    url: `/nr1/action-plans/${id}`,
    method: "delete",
  });
}

// ==================== RESPOSTAS (PORTAL) ====================

/**
 * Validar token de acesso
 */
export function validateToken(token) {
  return request({
    url: `/nr1/public/validate-token/${token}`,
    method: "get",
  });
}

/**
 * Submeter resposta
 */
export function submitResponse(token, data) {
  return request({
    url: `/nr1/public/submit/${token}`,
    method: "post",
    data,
  });
}
